import { create } from 'zustand';
import {
  OrderSchema,
  CreatePickupOrderRequest,
  UpdateOrderRequest,
  OrderQueryParams,
} from '../types/order.type';
import * as orderService from '../services/order.service';
import { useOrganizationStore } from './organization.store';
import { extractApiErrorDetail } from '../helpers/api-error.helper';

/** List filters for GET /pickup_order (organization_id is injected in fetchOrders). */
type PickupOrdersListQuery = Partial<
  Omit<OrderQueryParams, 'organization_id'>
> & { page?: number; limit?: number };

interface PickupOrderState {
  // Orders
  orders: OrderSchema[];
  ordersLoading: boolean;
  ordersError: string | null;
  ordersErrorCode: string | null;
  selectedOrder: OrderSchema | null;
  selectedOrderLoading: boolean;
  selectedOrderError: string | null;
  totalOrders: number;
  currentPage: number;
  hasMoreOrders: boolean;
  totalPages: number;
  /** Last successful list query; reused when fetchOrders() is called with no args (e.g. after delete). */
  lastOrdersListQuery: PickupOrdersListQuery | null;

  // Actions
  fetchOrders: (params?: PickupOrdersListQuery) => Promise<void>;
  fetchOrder: (orderId: string) => Promise<void>;
  createOrder: (order: CreatePickupOrderRequest) => Promise<void>;
  updateOrder: (orderId: string, order: UpdateOrderRequest) => Promise<void>;
  deleteOrder: (orderId: string) => Promise<void>;
  setSelectedOrder: (order: OrderSchema | null) => void;
  clearOrdersError: () => void;
  clearSelectedOrderError: () => void;
  clearSelectedOrder: () => void;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

export const usePickupOrderStore = create<PickupOrderState>((set, get) => ({
  // Initial state
  orders: [],
  ordersLoading: false,
  ordersError: null,
  ordersErrorCode: null,
  selectedOrder: null,
  selectedOrderLoading: false,
  selectedOrderError: null,
  totalOrders: 0,
  currentPage: 1,
  hasMoreOrders: false,
  totalPages: 0,
  lastOrdersListQuery: null,

  // Actions
  fetchOrders: async (params?: PickupOrdersListQuery) => {
    try {
      set({ ordersLoading: true, ordersError: null, ordersErrorCode: null });
      
      // Get the selected organization from the organization store
      const selectedOrg = useOrganizationStore.getState().selectedOrganization;

      const last = get().lastOrdersListQuery;
      const listInput: PickupOrdersListQuery =
        params === undefined
          ? {
              page: last?.page ?? 1,
              limit: last?.limit ?? 20,
              ...(last?.start_date ? { start_date: last.start_date } : {}),
              ...(last?.end_date ? { end_date: last.end_date } : {}),
              ...(last?.search ? { search: last.search } : {}),
              ...(last?.status ? { status: last.status } : {}),
              ...(last?.waste_type ? { waste_type: last.waste_type } : {}),
              ...(last?.sort_by ? { sort_by: last.sort_by } : {}),
              ...(last?.sort_pickup_order
                ? { sort_pickup_order: last.sort_pickup_order }
                : {}),
            }
          : {
              page: params.page ?? 1,
              limit: params.limit ?? 20,
              ...(params.start_date ? { start_date: params.start_date } : {}),
              ...(params.end_date ? { end_date: params.end_date } : {}),
              ...(params.search ? { search: params.search } : {}),
              ...(params.status ? { status: params.status } : {}),
              ...(params.waste_type ? { waste_type: params.waste_type } : {}),
              ...(params.sort_by ? { sort_by: params.sort_by } : {}),
              ...(params.sort_pickup_order
                ? { sort_pickup_order: params.sort_pickup_order }
                : {}),
            };

      const queryParams: OrderQueryParams = {
        ...listInput,
        page: listInput.page ?? 1,
        limit: listInput.limit ?? 20,
        organization_id: selectedOrg?.id || '',
      };
      
      const response = await orderService.getOrders(queryParams);
      const data = response as GenericResponse<{
        pickup_orders: OrderSchema[];
        total: number;
        page: number;
        limit: number;
      }>;
      
      // Calculate totalPages based on total and limit
      const limit = queryParams.limit || 20; // Default limit
      const totalPages = Math.ceil((data.result?.total || 0) / limit);
      
      set({
        orders: data.result?.pickup_orders || [],
        totalOrders: data.result?.total || 0,
        currentPage: data.result?.page || 1,
        totalPages: totalPages,
        hasMoreOrders: (data.result?.page || 1) < totalPages,
        ordersLoading: false,
        lastOrdersListQuery: {
          page: queryParams.page,
          limit: queryParams.limit,
          ...(queryParams.start_date ? { start_date: queryParams.start_date } : {}),
          ...(queryParams.end_date ? { end_date: queryParams.end_date } : {}),
          ...(queryParams.search ? { search: queryParams.search } : {}),
          ...(queryParams.status ? { status: queryParams.status } : {}),
          ...(queryParams.waste_type ? { waste_type: queryParams.waste_type } : {}),
          ...(queryParams.sort_by ? { sort_by: queryParams.sort_by } : {}),
          ...(queryParams.sort_pickup_order
            ? { sort_pickup_order: queryParams.sort_pickup_order }
            : {}),
        },
      });
    } catch (error) {
      set({
        ordersError: error instanceof Error ? error.message : 'Failed to fetch orders',
        ordersLoading: false,
      });
    }
  },

  fetchOrder: async (orderId: string) => {
    try {
      set({ selectedOrderLoading: true, selectedOrderError: null });
      const response = await orderService.getOrder(orderId);
      const data = response as GenericResponse<OrderSchema>;
      console.log('API response for order:', response);
      set({
        selectedOrder: data.result,
        selectedOrderLoading: false,
      });
    } catch (error) {
      console.error('Error fetching order:', error);
      set({
        selectedOrderError: error instanceof Error ? error.message : 'Failed to fetch order',
        selectedOrderLoading: false,
      });
    }
  },

  createOrder: async (order: CreatePickupOrderRequest) => {
    try {
      set({ ordersLoading: true, ordersError: null, ordersErrorCode: null });
      await orderService.createOrder(order);
      await get().fetchOrders();
    } catch (error) {
      const detail = extractApiErrorDetail(error, 'Failed to create order');
      set({
        ordersError: detail.message,
        ordersErrorCode: detail.code,
        ordersLoading: false,
      });
      throw error;
    }
  },

  updateOrder: async (orderId: string, order: UpdateOrderRequest) => {
    try {
      set({ ordersLoading: true, ordersError: null, ordersErrorCode: null });
      await orderService.updateOrder(orderId, order);
      await get().fetchOrders();
      if (get().selectedOrder?.id === orderId) {
        await get().fetchOrder(orderId);
      }
    } catch (error) {
      set({
        ordersError: error instanceof Error ? error.message : 'Failed to update order',
        ordersLoading: false,
      });
    }
  },

  deleteOrder: async (orderId: string) => {
    try {
      set({ ordersLoading: true, ordersError: null, ordersErrorCode: null });
      await orderService.deleteOrder(orderId);
      await get().fetchOrders();
      if (get().selectedOrder?.id === orderId) {
        set({ selectedOrder: null });
      }
    } catch (error) {
      set({
        ordersError: error instanceof Error ? error.message : 'Failed to delete order',
        ordersLoading: false,
      });
    }
  },

  setSelectedOrder: (order) => {
    set({ selectedOrder: order });
  },

  clearSelectedOrder: () => {
    set({ selectedOrder: null });
  },

  clearOrdersError: () => {
    set({ ordersError: null, ordersErrorCode: null });
  },

  clearSelectedOrderError: () => {
    set({ selectedOrderError: null });
  },
}));
